import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { getCustomerQuotations } from "./customerSlice";
import {
  addEpcOrder,
  approveNetMeterCostPaymentApi,
  approvePayment,
  confirmQuoteApi,
  createQuoteApi,
  addRepaymentTaskApi,
  fetchAllEpcPayments,
  fetchRegisteredUserInfo,
  getAllDetailsOfRegisteredCustomer,
  getAllEpcNamesIdsApi,
  getAllQuotes,
  getCustomerQuotesApi,
  getEpcMetaDataApi,
  getPaymentDetails,
  openQuoteApi,
  registerUser,
  rejectPayment,
  updateQuoteDiscountApi,
  updateProductRegistrationApi,
  updateRegisteredCustomerApi,
  uploadCustomerDocumentApi,
  updateKycDocumentApi,
  addEpcPaymentApi,
  updateQuoteAdjustmentAmountApi,
  updateRegistrationStatusApi,
  addRoundOffPaymentApi,
  addEpcOrderApi,
  updateEpcOrderApi,
  getSingleEpcQuoteApi,
  confirmEpcOrderApi,
  fetchSpecificRoleUsers,
  getSalesRepAssigneeLogs,
  updateOtherInverterApi,
  getSalesRepsUnderCSE,
  fetchCustomerByMobileNumber,
  deleteUser,
  updateQuoteStatusApi,
  getStatusHistoryApi,
  activateQuoteApi,
  deductSalesRepCommissionApi,
} from "../../api/epc.api";
import {
  ICommissionType,
  ICreateQuotationForm,
  ICustomer,
  IEpcMetaData,
  IEpcPayment,
  IEpcQuotation,
  IRegisteredUser,
  IRegisteredUserWithDetails,
  ISalesRepAssigneeLog,
  ISalesRepInfo,
  RegistrationStatus,
  ICSEWithSalesReps,
  QuoteStatusHistory,
} from "../../models/epcCustomer.model";
import {
  addSalesRepApi,
  getSalesRepBasedOnUser,
  getSingleSalesRepById,
  updateSalesRepById,
} from "../../api/salesRep.api";
import { IUserRef } from "../../models/userData.model";
import ResponseModel, { PaginationRes } from "../../models/response.model";

interface EpcState {
  epcPaymentsPagination: PaginationRes;
  epcMetaData: IEpcMetaData | null;
  orderNumber: string;
  epcPayments: IEpcPayment[];
  customerPayments: IEpcPayment[];
  epc: ICustomer[];
  salesReps: ISalesRepInfo[];
  epcQuotes: IEpcQuotation[];
  customerQuotes: IEpcQuotation[];
  registeredCustomers: IRegisteredUser[];
  registeredUserInfoWithDetails: IRegisteredUserWithDetails[];
  currentPageOfRegisteredUsers: number;
  totalPagesOfRegisteredUsers: number;
  currentPageOfQuotes: number;
  totalPagesOfQuotes: number;

  loading: boolean;
  loadingState: {
    activateQuoteloading: boolean;
    fetchtingCutomerDetailsLoading: boolean;
    fetchingPaymentsLoading: boolean;
    deleteCustomerLoading: boolean;
    approvingPaymentLoading: boolean;
    rejectingPaymentLoading: boolean;
    creatingEpcQuoteLoading: boolean;
    fetchingEpcMetaDataLoading: boolean;
    approvingNetMeterCostLoading: boolean;
    fetchingSalesRepLoading: boolean;
    fetchingCustomerQuotesLoading?: boolean;
    updatingRegisteredCustomerLoading?: boolean;
    fetchingNextPageOfRegisteredUsersLoading?: boolean;
    confirmingQuoteLoading?: boolean;
    openingQuoteLoading?: boolean;
    uploadingDocumentLoading: boolean;
    addingRepaymentLoading: boolean;
    updatingProductRegistrationLoading: boolean;
    verifyingDocumentLoading: Record<string, boolean>;
    addingPaymentLoading: boolean;
    updatingAdjustmentAmountLoading?: boolean;
    updatingRegistrationStatusLoading?: boolean;
    addingRoundOffLoading: boolean;
    addingSalesRepLoading?: boolean;
    addingEpcOrderLoading: boolean;
    fetchingSpecificRoleUsersLoading?: boolean;
    fetchingAssigneeLogsLoading?: boolean;
    updatingOtherInverterLoading?: boolean;
    fetchingCSEWithSalesRepsLoading?: boolean;
    fetchingCustomerByMobileNumberLoading?: boolean;
    fetchStatusHistoryLoading?: boolean;
    deductSalesRepCommissionLoading?: boolean;
  };
  error: string | null;
  currentEpcCustomer: {
    customerData: IRegisteredUserWithDetails | null;
    customerPayment: IEpcPayment | null;
    allPayments?: IEpcPayment[] | null;
  };
  searchTerm: string;
  selectedSalesRep: string | undefined;
  selectedCseIdForCustomerDetails?: string | undefined;
  salesRep: ISalesRepInfo | null;
  specificRoleUsers: IUserRef[];
  salesRepAssigneeLogs: ISalesRepAssigneeLog[];
  createEpcQuoteStatus?: "idle" | "pending" | "fulfilled" | "rejected";
  createEpcQuoteError?:
    | string
    | {
        type: "CUSTOMER_NOT_FOUND";
        message: string;
        data: ICreateQuotationForm;
      }
    | null;
  pendingQuoteData?: ICreateQuotationForm | null;
  cseWithSalesReps: ICSEWithSalesReps[];
  selectedCSE: string | undefined;
  selectedStatus: string | undefined;
  salesRepOptions: ISalesRepInfo[];
  customerByMobileNumber: IRegisteredUser | null;
  totalPagesOfSalesReps: number;
  currentPageOfSalesReps: number;
  statusHistory: QuoteStatusHistory[];
}

const initialState: EpcState = {
  epcPaymentsPagination: {
    totalPages: 1,
    totalCount: 0,
    page: 1,
  },
  currentEpcCustomer: {
    customerData: null,
    customerPayment: null,
  },
  orderNumber: "",
  epcMetaData: null,
  customerQuotes: [],
  epcPayments: [],
  registeredCustomers: [],
  loading: false,
  salesReps: [],
  epc: [],
  loadingState: {
    activateQuoteloading: false,
    fetchtingCutomerDetailsLoading: false,
    deleteCustomerLoading: false,
    fetchingPaymentsLoading: false,
    approvingPaymentLoading: false,
    rejectingPaymentLoading: false,
    fetchingEpcMetaDataLoading: false,
    approvingNetMeterCostLoading: false,
    creatingEpcQuoteLoading: false,
    fetchingSalesRepLoading: false,
    fetchingNextPageOfRegisteredUsersLoading: false,
    fetchingCustomerQuotesLoading: false,
    openingQuoteLoading: false,
    uploadingDocumentLoading: false,
    addingRepaymentLoading: false,
    updatingProductRegistrationLoading: false,
    verifyingDocumentLoading: {},
    addingPaymentLoading: false,

    updatingAdjustmentAmountLoading: false,
    updatingRegistrationStatusLoading: false,
    addingRoundOffLoading: false,
    addingSalesRepLoading: false,
    addingEpcOrderLoading: false,
    fetchingSpecificRoleUsersLoading: false,
    fetchingAssigneeLogsLoading: false,
    updatingOtherInverterLoading: false,
    fetchingCSEWithSalesRepsLoading: false,
    fetchingCustomerByMobileNumberLoading: false,
    fetchStatusHistoryLoading: false,
  },
  currentPageOfRegisteredUsers: 1,
  totalPagesOfRegisteredUsers: 1,
  currentPageOfQuotes: 1,
  totalPagesOfQuotes: 1,
  error: null,
  epcQuotes: [],
  customerPayments: [],
  registeredUserInfoWithDetails: [],
  searchTerm: "",
  selectedSalesRep: undefined,
  selectedCseIdForCustomerDetails: undefined,
  salesRep: null,
  specificRoleUsers: [],
  salesRepAssigneeLogs: [],
  createEpcQuoteStatus: "idle",
  createEpcQuoteError: null,
  pendingQuoteData: null,
  cseWithSalesReps: [],
  selectedCSE: undefined,
  selectedStatus: undefined,
  salesRepOptions: [],
  customerByMobileNumber: null,
  totalPagesOfSalesReps: 1,
  currentPageOfSalesReps: 1,
  statusHistory: [],
};

export const EpcOrder = createAsyncThunk(
  "epc/EpcOrder",
  async (epcData: any, { rejectWithValue, dispatch }) => {
    try {
      const res = await addEpcOrder(
        epcData.customerId,
        epcData.name,
        epcData.email,
        epcData.mobileNumber,
        epcData.billingAddress,
        epcData.shippingAddress,
        epcData.category,
        epcData.gstin,
        epcData.goods,
        epcData.quotationId,
        epcData.transportPrice || 0,
      );
      dispatch(getCustomerQuotations(epcData.customerId));
      return res;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to add EPC");
    }
  },
);

// This all thunks are for whatsapp chatbot
export const getAllEpcPayments = createAsyncThunk(
  "epc/getAllEpcPayments",
  async (
    params: {
      searchQuery?: string;
      page: number;
      limit?: number;
      paymentStatus?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchAllEpcPayments(
        params.page,
        params.limit || 12,
        params.paymentStatus,
        params.searchQuery,
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        (error as any).response?.data || "Failed to fetch EPC payments",
      );
    }
  },
);

export const getStatusHistory = createAsyncThunk(
  "epc/getStatusHistory",
  async (custId: string, { rejectWithValue }) => {
    try {
      const response = await getStatusHistoryApi(custId);
      return response;
    } catch (error) {
      return rejectWithValue(
        (error as any).response?.data || "Failed to fetch EPC payments",
      );
    }
  },
);
export const updateQuoteStatus = createAsyncThunk(
  "epc/updateQuoteStatus",
  async (
    params: {
      quoteId: string;
      status: string;
      remark?: string;
      loanDetails?: {
        bankName: String;
        dateApplied: Date;
        approvedDate?: Date;
        rejectedDate?: Date;
        approvalLetter?: String;
        isJansmarath?: boolean;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateQuoteStatusApi(
        params.quoteId,
        params.status,
        params.remark,
        params.loanDetails,
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        (error as any).response?.data || "Failed to fetch EPC payments",
      );
    }
  },
);

export const getAllEpcRegisteredUsers = createAsyncThunk(
  "epc/getAllEpcRegisteredUsers",
  async () => {
    try {
      const response = await fetchRegisteredUserInfo();
      return response;
    } catch (error) {
      return (
        (error as any).response?.data || "Failed to fetch EPC registered users"
      );
    }
  },
);
export const getDetailsOfRegisteredUserWithPaymentDetails = createAsyncThunk(
  "epc/getDetailsOfRegisteredUserWithPaymentDetails",
  async (params?: {
    salesRepId?: string;
    status?: string;
    cseID?: string;
    search?: string;
    page?: number;
    startDate?: string;
    endDate?: string;
    panelBrand?: string;
    plantSize?: string;
    registrationStatus?: string;
  }) => {
    try {
      const response = await getAllDetailsOfRegisteredCustomer(
        params?.salesRepId,
        params?.status,
        params?.cseID,
        params?.search,
        params?.page || 1,
        params?.startDate,
        params?.endDate,
        params?.panelBrand,
        params?.plantSize,
        params?.registrationStatus,
      );
      return response;
    } catch (error: any) {
      return error.response?.data || "Failed to fetch EPC registered users";
    }
  },
);

export const getPaymentDetailsBasedonCustomerMobile = createAsyncThunk(
  "epc/getPaymentDetailsBasedonCustomerMobile",
  async (customerMobile: string) => {
    try {
      const response = await getPaymentDetails(customerMobile);
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to fetch payment details";
    }
  },
);

export const addNewCustomer = createAsyncThunk(
  "epc/addNewCustomer",
  async (customerData: IRegisteredUser) => {
    try {
      const response = await registerUser(customerData);
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to add new customer";
    }
  },
);

export const deleteCustomer = createAsyncThunk(
  "epc/deleteCustomer",
  async (customerId: string) => {
    try {
      const response = await deleteUser(customerId);
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to add new customer";
    }
  },
);

// approval and rejection of payments Of EPC
export const approvePaymentById = createAsyncThunk<
  any,
  { paymentId: string },
  { rejectValue: string }
>("epc/approvePayment", async ({ paymentId }, { rejectWithValue }) => {
  try {
    const response = await approvePayment(paymentId);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to approve payment",
    );
  }
});

export const rejectPaymentById = createAsyncThunk(
  "epc/rejectPayment",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await rejectPayment(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject payment",
      );
    }
  },
);

export const getAllEpcQuotes = createAsyncThunk(
  "epc/getAllEpcQuotes",
  async (params?: {
    search?: string;
    page?: number;
    panelBrand?: string;
    plantSize?: string;
    isConfirmed?: boolean;
    panelType?: string;
    salesRepId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const response = await getAllQuotes(
        params?.search,
        params?.page || 1,
        params?.panelBrand,
        params?.plantSize,
        params?.isConfirmed,
        params?.panelType,
        params?.salesRepId,
        params?.startDate,
        params?.endDate,
      );
      return response;
    } catch (error: any) {
      return error?.response?.data || "Failed to fetch EPC quotes";
    }
  },
);

export const createEpcQuote = createAsyncThunk(
  "epc/createEpcQuote",
  async (quoteData: ICreateQuotationForm, { rejectWithValue }) => {
    try {
      const response = await createQuoteApi(
        quoteData.customerName,
        quoteData.mobileNumber,
        Number(quoteData.subsidyAmount),
        quoteData.brand,
        quoteData.panelType,
        quoteData.plantSize,
        Number(quoteData.plantCost),
        Number(quoteData.meterCost),
        quoteData.salesRepresentative!,
        quoteData.cseID,
      );
      return response;
    } catch (error: any) {
      // Check if it's a "customer not found" error
      if (
        error?.message?.includes("Customer not found") ||
        error?.response?.data?.message?.includes("Customer not found")
      ) {
        return rejectWithValue({
          type: "CUSTOMER_NOT_FOUND",
          message: "Customer not found with this mobile number",
          data: quoteData,
        });
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to create EPC quote",
      );
    }
  },
);

export const getCustomerQuotes = createAsyncThunk(
  "epc/getCustomerQuotes",
  async (mobileNumber: string) => {
    try {
      const response = await getCustomerQuotesApi(mobileNumber);
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to fetch customer quotes";
    }
  },
);

export const addSalesRep = createAsyncThunk(
  "epc/addSalesRep",
  async (salesRepData: ISalesRepInfo, { rejectWithValue }) => {
    try {
      const response = await addSalesRepApi(salesRepData);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to add sales rep",
      );
    }
  },
);

export const getSalesRep = createAsyncThunk(
  "epc/getSalesRep",
  async (params: {
    search?: string;
    category?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const response = await getSalesRepBasedOnUser(
        params?.search,
        params?.category,
        params?.page,
        params?.limit,
      );
      return response;
    } catch (error: any) {
      return error(error || "Failed to fetch sales representative");
    }
  },
);

export const getEpcMetaData = createAsyncThunk(
  "epc/getEpcMetaData",
  async () => {
    try {
      const response = await getEpcMetaDataApi();
      return response;
    } catch (error) {
      return (error as any).response?.data || "Failed to fetch EPC meta data";
    }
  },
);

export const approveNetMeterFileCostPayment = createAsyncThunk(
  "epc/approveNetMeterFileCostPayment",
  async (customerId: string) => {
    try {
      const response = await approveNetMeterCostPaymentApi(customerId);
      return response;
    } catch (error) {
      return (
        (error as any).response?.data ||
        "Failed to approve net meter file cost payment"
      );
    }
  },
);

export const fetchSingleSalesRep = createAsyncThunk(
  "epc/fetchSingleSalesRep",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getSingleSalesRepById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch sales rep");
    }
  },
);

export const updateSalesRep = createAsyncThunk(
  "epc/updateSalesRep",
  async (
    { id, updatedData }: { id: string; updatedData: Partial<ISalesRepInfo> },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateSalesRepById(id, updatedData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update sales rep");
    }
  },
);

export const updateRegisteredCustomer = createAsyncThunk(
  "epc/updateRegisteredCustomer",
  async (
    {
      customerId,
      customerName,
      mobileNumber,
      email,
      cseId,
      salesRepId,
    }: {
      customerId: string;
      customerName: string;
      mobileNumber: string;
      email: string;
      cseId?: string;
      salesRepId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateRegisteredCustomerApi(
        customerId,
        customerName,
        mobileNumber,
        email,
        cseId,
        salesRepId,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update registered customer");
    }
  },
);

export const confirmQuote = createAsyncThunk(
  "epc/confirmQuote",
  async (
    {
      quoteId,
      customCommission,
      customCommissionType,
      excessAmount,
    }: {
      quoteId: string;
      customCommission?: number;
      customCommissionType?: ICommissionType;
      excessAmount?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await confirmQuoteApi(
        quoteId,
        customCommission,
        customCommissionType,
        excessAmount,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to confirm quote");
    }
  },
);

export const getAllEpcNamesIds = createAsyncThunk(
  "warranty/getAllEpcNamesIds",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllEpcNamesIdsApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch EPC names and IDs",
      );
    }
  },
);

export const openQuote = createAsyncThunk(
  "epc/openQuote",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await openQuoteApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to open quote PDF");
    }
  },
);

export const updateQuoteDiscount = createAsyncThunk(
  "epc/updateQuoteDiscount",
  async (
    { quoteId, discount }: { quoteId: string; discount: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateQuoteDiscountApi(quoteId, discount);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update quote discount");
    }
  },
);

export const uploadCustomerDocument = createAsyncThunk(
  "epc/uploadCustomerDocument",
  async (
    {
      customerId,
      documentName,
      file,
    }: {
      customerId: string;
      documentName: string;
      file: File;
    },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("customerId", customerId);
      formData.append("documentName", documentName);

      const response = await uploadCustomerDocumentApi(formData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload document",
      );
    }
  },
);
export const updateProductRegistration = createAsyncThunk(
  "epc/updateProductRegistration",
  async (
    {
      customerId,
      formData,
    }: {
      customerId: string;
      formData: FormData;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateProductRegistrationApi(customerId, formData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to update product registration",
      );
    }
  },
);

export const addRepaymentTask = createAsyncThunk(
  "epc/createRepayment",
  async (
    {
      mobileNumber,
      customerName,
      totalCost,
      quotationId,
    }: {
      mobileNumber: string;
      customerName: string;
      totalCost: number;
      quotationId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await addRepaymentTaskApi(
        mobileNumber,
        customerName,
        totalCost,
        quotationId,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to create repayment");
    }
  },
);

export const updateKycDocument = createAsyncThunk(
  "epc/verifyKycDocument",
  async (
    {
      customerId,
      documentId,
      isVerified,
    }: {
      customerId: string;
      documentId: string;
      isVerified: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateKycDocumentApi(
        customerId,
        documentId,
        isVerified,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to verify document",
      );
    }
  },
);

export const addEpcPayment = createAsyncThunk(
  "epc/addEpcPayment",
  async (
    paymentData: {
      customerMobileNumber: string;
      amount: number;
      netMeterPayment?: number;
      file: File;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await addEpcPaymentApi(paymentData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to add payment");
    }
  },
);

export const updateQuoteAdjustmentAmount = createAsyncThunk(
  "epc/updateQuoteAdjustmentAmount",
  async (
    {
      quoteId,
      adjustmentAmount,
      adjustmentRemark,
    }: { quoteId: string; adjustmentAmount: number; adjustmentRemark: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateQuoteAdjustmentAmountApi(
        quoteId,
        adjustmentAmount,
        adjustmentRemark,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update adjustment amount");
    }
  },
);

export const updateRegistrationStatus = createAsyncThunk(
  "epc/updateRegistrationStatus",
  async (
    {
      customerId,
      registrationStatus,
      registrationRemark,
    }: {
      customerId: string;
      registrationStatus: RegistrationStatus;
      registrationRemark?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateRegistrationStatusApi(
        customerId,
        registrationStatus,
        registrationRemark,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to update registration status",
      );
    }
  },
);

export const addRoundOffToPayment = createAsyncThunk(
  "epc/addRoundOffToPayment",
  async (
    {
      customerMobileNumber,
      roundOffAmount,
    }: { customerMobileNumber: string; roundOffAmount: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await addRoundOffPaymentApi(
        customerMobileNumber,
        roundOffAmount,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to add round-off amount");
    }
  },
);

export const getSingleEpcQuoteThunk = createAsyncThunk(
  "epc/getSingleQuote",
  async (quotationId: string, { rejectWithValue }) => {
    try {
      return await getSingleEpcQuoteApi(quotationId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch quote",
      );
    }
  },
);

export const addEpcOrderThunk = createAsyncThunk(
  "epc/addEpcOrder",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await addEpcOrderApi(payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add order",
      );
    }
  },
);

export const updateEpcOrderThunk = createAsyncThunk(
  "epc/updateEpcOrder",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await updateEpcOrderApi(payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update order",
      );
    }
  },
);

export const confirmEpcOrderThunk = createAsyncThunk(
  "epc/confirmOrder",
  async (quotationId: string, { rejectWithValue }) => {
    try {
      return await confirmEpcOrderApi(quotationId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to confirm order",
      );
    }
  },
);

export const getSpecificRoleUsers = createAsyncThunk(
  "users/getSpecificRoleUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchSpecificRoleUsers();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch users");
    }
  },
);

export const fetchSalesRepAssigneeLogs = createAsyncThunk(
  "epc/getSalesRepAssigneeLogs",
  async (salesRepId: string, { rejectWithValue }) => {
    try {
      const response = await getSalesRepAssigneeLogs(salesRepId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch assignee logs");
    }
  },
);

export const updateOtherInverter = createAsyncThunk(
  "epc/updateOtherInverter",
  async (
    { quoteId, otherInverter }: { quoteId: string; otherInverter: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateOtherInverterApi(quoteId, otherInverter);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update other inverter");
    }
  },
);

export const fetchSalesRepsUnderCSE = createAsyncThunk(
  "epc/fetchCSEWithSalesReps",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSalesRepsUnderCSE();
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch CSE and sales reps");
    }
  },
);

export const getCustomerByMobileNumber = createAsyncThunk(
  "epc/getCustomerByMobileNumber",
  async (mobileNumber: string, { rejectWithValue }) => {
    try {
      const response = await fetchCustomerByMobileNumber(mobileNumber);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customer details",
      );
    }
  },
);
export const activateQuote = createAsyncThunk<
  ResponseModel,
  {
    quoteId: string;
  },
  { rejectValue: string }
>("newFloor/activateQoute", async ({ quoteId }, { rejectWithValue }) => {
  try {
    return await activateQuoteApi(quoteId);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to activate quote");
  }
});

export const deductSalesRepCommission = createAsyncThunk<
  ResponseModel,
  {
    salesRepId: string;
    customerId: string;
    amount: number;
  },
  { rejectValue: string }
>(
  "epc/deductSalesRepCommission",
  async ({ salesRepId, customerId, amount }, { rejectWithValue }) => {
    try {
      return await deductSalesRepCommissionApi(salesRepId, customerId, amount);
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to deduct sales rep commission",
      );
    }
  },
);

const epcSlice = createSlice({
  name: "epc",
  initialState,
  reducers: {
    setCurrentEpcCustomer: (state, action) => {
      state.currentEpcCustomer = action.payload;
    },
    setSearchTerm: (state, action: { payload: string }) => {
      state.searchTerm = action.payload;
    },
    setSelectedSalesRep: (state, action: { payload: string | undefined }) => {
      state.selectedSalesRep = action.payload;
    },
    setSelectedCseIdForCustomerDetails: (
      state,
      action: { payload: string | undefined },
    ) => {
      state.selectedCseIdForCustomerDetails = action.payload;
    },
    clearSearchAndSelectedSalesRep: (state) => {
      state.searchTerm = "";
      state.selectedSalesRep = undefined;
      state.selectedCseIdForCustomerDetails = undefined;
    },
    resetCreateEpcQuoteState: (state) => {
      state.createEpcQuoteStatus = "idle";
      state.createEpcQuoteError = null;
      state.pendingQuoteData = null;
    },
    setSelectedStatus: (state, action) => {
      state.selectedStatus = action.payload;
    },
    setSelectedCSE: (state, action) => {
      state.selectedCSE = action.payload;
    },

    setSalesRepOptions: (state, action) => {
      state.salesRepOptions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(EpcOrder.pending, (state) => {
        state.loadingState.fetchingPaymentsLoading = true;
        state.error = null;
      })
      .addCase(EpcOrder.fulfilled, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.orderNumber = action.payload.orderNumber;
      })

      .addCase(EpcOrder.rejected, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getStatusHistory.pending, (state) => {
        state.loadingState.fetchStatusHistoryLoading = true;
      })
      .addCase(getStatusHistory.fulfilled, (state, action) => {
        state.loadingState.fetchStatusHistoryLoading = false;
        state.statusHistory = action.payload.data;
      })
      .addCase(getStatusHistory.rejected, (state, action) => {
        state.loadingState.fetchStatusHistoryLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllEpcPayments.pending, (state) => {
        state.loadingState.fetchingPaymentsLoading = true;
      })
      .addCase(getAllEpcPayments.fulfilled, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.epcPayments = action.payload.data.payments;
        state.epcPaymentsPagination = action.payload.pagination!;
      })
      .addCase(getAllEpcPayments.rejected, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllEpcRegisteredUsers.pending, (state) => {
        state.loadingState.fetchingPaymentsLoading = true;
      })
      .addCase(getAllEpcRegisteredUsers.fulfilled, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.registeredCustomers = action.payload;
      })
      .addCase(getAllEpcRegisteredUsers.rejected, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getPaymentDetailsBasedonCustomerMobile.pending, (state) => {
        state.loadingState.fetchingPaymentsLoading = true;
      })
      .addCase(
        getPaymentDetailsBasedonCustomerMobile.fulfilled,
        (state, action) => {
          state.loadingState.fetchingPaymentsLoading = false;
          state.customerPayments = action.payload;
        },
      )
      .addCase(
        getPaymentDetailsBasedonCustomerMobile.rejected,
        (state, action) => {
          state.loadingState.fetchingPaymentsLoading = false;
          state.error = action.payload as string;
        },
      )
      .addCase(addNewCustomer.pending, (state) => {
        state.loadingState.fetchingPaymentsLoading = true;
        state.error = null;
      })
      .addCase(addNewCustomer.fulfilled, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.error = null;
        state.registeredCustomers.push(action.payload.data);
      })
      .addCase(addNewCustomer.rejected, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.error =
          (action.payload as string) || "Failed to add new customer";
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.loadingState.deleteCustomerLoading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.loadingState.deleteCustomerLoading = false;
        state.error = null;
        state.registeredUserInfoWithDetails =
          state.registeredUserInfoWithDetails.filter(
            (customer) => customer._id !== action.payload.data.customerId,
          );
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.loadingState.deleteCustomerLoading = false;
        state.error = (action.payload as string) || "Failed to delete customer";
      })
      .addCase(
        getDetailsOfRegisteredUserWithPaymentDetails.pending,
        (state) => {
          state.loadingState.fetchtingCutomerDetailsLoading = true;
          state.loadingState.fetchingPaymentsLoading = true;
          state.loadingState.fetchingNextPageOfRegisteredUsersLoading = true;
        },
      )
      .addCase(
        getDetailsOfRegisteredUserWithPaymentDetails.fulfilled,
        (state, action) => {
          state.loadingState.fetchingPaymentsLoading = false;
          state.loadingState.fetchingNextPageOfRegisteredUsersLoading = false;
          state.loadingState.fetchtingCutomerDetailsLoading = false;
          if (action.payload) {
            state.registeredUserInfoWithDetails = action.payload.data.items;
            state.currentPageOfRegisteredUsers = action.payload.pagination.page;
            state.totalPagesOfRegisteredUsers =
              action.payload.pagination.totalPages;
            state.currentEpcCustomer.allPayments =
              action.payload.data.allPayments || null;
          }
        },
      )
      .addCase(
        getDetailsOfRegisteredUserWithPaymentDetails.rejected,
        (state, action) => {
          state.loadingState.fetchingPaymentsLoading = false;
          state.loadingState.fetchingNextPageOfRegisteredUsersLoading = false;
          state.loadingState.fetchtingCutomerDetailsLoading = false;
          state.error = action.payload as string;
        },
      )
      .addCase(approvePaymentById.pending, (state) => {
        state.error = null;
        state.loadingState.approvingPaymentLoading = true;
      })
      .addCase(approvePaymentById.fulfilled, (state, action) => {
        state.loadingState.approvingPaymentLoading = false;

        const updatedEpcPayment = action.payload;

        // update list
        state.epcPayments = state.epcPayments.map((epcPayment) =>
          String(epcPayment._id) === String(updatedEpcPayment._id)
            ? updatedEpcPayment
            : epcPayment,
        );

        // update selected customer
        if (
          state.currentEpcCustomer?.customerPayment &&
          String(state.currentEpcCustomer.customerPayment._id) ===
            String(updatedEpcPayment._id)
        ) {
          state.currentEpcCustomer.customerPayment = updatedEpcPayment;
        }
      })
      .addCase(approvePaymentById.rejected, (state, action) => {
        state.loadingState.approvingPaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(rejectPaymentById.pending, (state) => {
        state.error = null;
        state.loadingState.rejectingPaymentLoading = true;
      })
      .addCase(rejectPaymentById.fulfilled, (state, action) => {
        state.loadingState.rejectingPaymentLoading = false;

        const updatedEpcPayment = action.payload;

        // update list
        state.epcPayments = state.epcPayments.map((epcPayment) =>
          String(epcPayment._id) === String(updatedEpcPayment._id)
            ? updatedEpcPayment
            : epcPayment,
        );

        // update selected customer
        if (
          state.currentEpcCustomer?.customerPayment &&
          String(state.currentEpcCustomer.customerPayment._id) ===
            String(updatedEpcPayment._id)
        ) {
          state.currentEpcCustomer.customerPayment = updatedEpcPayment;
        }
      })
      .addCase(rejectPaymentById.rejected, (state, action) => {
        state.loadingState.rejectingPaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllEpcQuotes.pending, (state) => {
        state.loadingState.fetchingPaymentsLoading = true;
        state.loadingState.fetchingCustomerQuotesLoading = true;
      })
      .addCase(getAllEpcQuotes.fulfilled, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.loadingState.fetchingCustomerQuotesLoading = false;
        if (action?.payload?.quotes) {
          state.epcQuotes = action.payload.quotes;
          state.currentPageOfQuotes = action.payload.currentPage;
          state.totalPagesOfQuotes = action.payload.pagination.totalPages;
        }
      })
      .addCase(getAllEpcQuotes.rejected, (state, action) => {
        state.loadingState.fetchingPaymentsLoading = false;
        state.loadingState.fetchingCustomerQuotesLoading = false;

        state.error = action.payload as string;
      })
      .addCase(addSalesRep.pending, (state) => {
        state.loadingState.addingSalesRepLoading = true;
        state.error = null;
      })
      .addCase(addSalesRep.fulfilled, (state, action) => {
        state.loadingState.addingSalesRepLoading = false;
        state.salesReps.push(action.payload);
      })
      .addCase(addSalesRep.rejected, (state, action) => {
        state.loadingState.addingSalesRepLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getSalesRep.pending, (state) => {
        state.loadingState.fetchingSalesRepLoading = true;
        state.error = null;
      })
      .addCase(getSalesRep.fulfilled, (state, action) => {
        state.loadingState.fetchingSalesRepLoading = false;
        if (action.payload && typeof action.payload === "object") {
          if (action.payload.salesReps) {
            state.salesReps = action.payload.salesReps;
          } else {
            state.salesReps = action.payload;
          }

          if (action.payload.pagination) {
            state.currentPageOfSalesReps =
              action.payload.pagination.currentPage;
            state.totalPagesOfSalesReps = action.payload.pagination.totalPages;
          }
        } else {
          state.salesReps = action.payload;
        }
      })
      .addCase(getSalesRep.rejected, (state, action) => {
        state.loadingState.fetchingSalesRepLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getEpcMetaData.pending, (state) => {
        state.loadingState.fetchingEpcMetaDataLoading = true;
        state.error = null;
      })
      .addCase(getEpcMetaData.fulfilled, (state, action) => {
        state.loadingState.fetchingEpcMetaDataLoading = false;
        state.epcMetaData = action.payload;
      })
      .addCase(getEpcMetaData.rejected, (state, action) => {
        state.loadingState.fetchingEpcMetaDataLoading = false;
        state.error = action.payload as string;
      })
      .addCase(approveNetMeterFileCostPayment.pending, (state) => {
        state.loadingState.approvingNetMeterCostLoading = true;
        state.error = null;
      })
      .addCase(approveNetMeterFileCostPayment.fulfilled, (state) => {
        state.loadingState.approvingNetMeterCostLoading = false;
      })
      .addCase(approveNetMeterFileCostPayment.rejected, (state, action) => {
        state.loadingState.approvingNetMeterCostLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSingleSalesRep.pending, (state) => {
        state.loadingState.fetchingSalesRepLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleSalesRep.fulfilled, (state, action) => {
        state.loadingState.fetchingSalesRepLoading = false;
        state.salesRep = action.payload;
      })
      .addCase(fetchSingleSalesRep.rejected, (state, action) => {
        state.loadingState.fetchingSalesRepLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSalesRep.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSalesRep.fulfilled, (state, action) => {
        state.loading = false;

        const updatedRep = action.payload;
        state.salesReps = state.salesReps.map((rep) =>
          rep._id === updatedRep._id ? updatedRep : rep,
        );

        if (state.salesRep?._id === updatedRep._id) {
          state.salesRep = updatedRep;
        }
      })
      .addCase(updateSalesRep.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateRegisteredCustomer.pending, (state) => {
        state.loadingState.updatingRegisteredCustomerLoading = true;
        state.error = null;
      })
      .addCase(updateRegisteredCustomer.fulfilled, (state, action) => {
        state.loadingState.updatingRegisteredCustomerLoading = false;
      })
      .addCase(updateRegisteredCustomer.rejected, (state, action) => {
        state.loadingState.updatingRegisteredCustomerLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getCustomerQuotes.pending, (state) => {
        state.loadingState.fetchingCustomerQuotesLoading = true;
      })
      .addCase(getCustomerQuotes.fulfilled, (state, action) => {
        state.loadingState.fetchingCustomerQuotesLoading = false;
        state.customerQuotes = action.payload;
      })
      .addCase(getCustomerQuotes.rejected, (state, action) => {
        state.loadingState.fetchingCustomerQuotesLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createEpcQuote.pending, (state) => {
        state.loadingState.creatingEpcQuoteLoading = true;
        state.createEpcQuoteStatus = "pending";
        state.createEpcQuoteError = null;
        state.error = null;
      })
      .addCase(createEpcQuote.fulfilled, (state, action) => {
        state.loadingState.creatingEpcQuoteLoading = false;
        state.createEpcQuoteStatus = "fulfilled";
        state.createEpcQuoteError = null;
        if (action?.payload) {
          state?.epcQuotes?.push(action?.payload);

          const newQuote = action.payload.quotation || action.payload;
          const customerIndex = state.registeredUserInfoWithDetails.findIndex(
            (c) => c.mobileNumber === newQuote.mobileNumber,
          );

          if (customerIndex !== -1) {
            state.registeredUserInfoWithDetails[customerIndex] = {
              ...state.registeredUserInfoWithDetails[customerIndex],
              quote: newQuote,
              latestQuoteId: newQuote._id,
              isLatestQuoteConfirmed: false,
            };
          }
        }
      })
      .addCase(createEpcQuote.rejected, (state, action) => {
        state.loadingState.creatingEpcQuoteLoading = false;
        state.createEpcQuoteStatus = "rejected";
        if (action.payload && typeof action.payload === "object") {
          // If it's our custom error object with type and data
          state.createEpcQuoteError = action.payload as any;
        } else if (typeof action.payload === "string") {
          // If it's a string error message
          state.createEpcQuoteError = action.payload;
        } else {
          // Fallback for other error types
          state.createEpcQuoteError =
            action.error?.message || "Failed to create quote";
        }
      })
      .addCase(getAllEpcNamesIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEpcNamesIds.fulfilled, (state, action) => {
        state.loading = false;
        state.epc = action.payload.map(
          (item: { _id: string; name: string }) => ({
            _id: item._id,
            name: item.name,
          }),
        ) as ICustomer[];
      })
      .addCase(getAllEpcNamesIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(confirmQuote.pending, (state) => {
        state.loadingState.confirmingQuoteLoading = true;
        state.error = null;
      })
      .addCase(confirmQuote.fulfilled, (state, action) => {
        state.loadingState.confirmingQuoteLoading = false;
        const updatedQuote = action.payload.quotation;
        if (!updatedQuote?._id) return;

        state.customerQuotes = state.customerQuotes.map((q) =>
          q._id === updatedQuote._id ? { ...q, ...updatedQuote } : q,
        );

        state.epcQuotes = state.epcQuotes.map((q) =>
          q._id === updatedQuote._id ? { ...q, ...updatedQuote } : q,
        );

        const sortedCustomerQuotes = [...state.customerQuotes].sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime(),
        );

        const absoluteLatestQuoteId =
          sortedCustomerQuotes.length > 0 ? sortedCustomerQuotes[0]._id : null;
        const isConfirmingAbsoluteLatest =
          absoluteLatestQuoteId === updatedQuote._id;

        const customerIndex = state.registeredUserInfoWithDetails.findIndex(
          (c) => c.mobileNumber === updatedQuote.mobileNumber,
        );

        if (customerIndex !== -1) {
          const customer = state.registeredUserInfoWithDetails[customerIndex];

          if (isConfirmingAbsoluteLatest) {
            (
              state.registeredUserInfoWithDetails[customerIndex] as any
            ).isLatestQuoteConfirmed = true;
          }

          const existingQuoteDate = new Date(
            customer.quote?.createdAt || 0,
          ).getTime();
          const updatedQuoteDate = new Date(
            updatedQuote.createdAt || 0,
          ).getTime();

          if (!customer.quote || updatedQuoteDate >= existingQuoteDate) {
            state.registeredUserInfoWithDetails[customerIndex].quote = {
              ...customer.quote,
              ...updatedQuote,
            } as any;
          }
        }
      })
      .addCase(confirmQuote.rejected, (state, action) => {
        state.loadingState.confirmingQuoteLoading = false;
        state.error = action.payload as string;
      })
      .addCase(openQuote.pending, (state) => {
        state.loadingState.openingQuoteLoading = true;
        state.error = null;
      })
      .addCase(openQuote.fulfilled, (state) => {
        state.loadingState.openingQuoteLoading = false;
      })
      .addCase(openQuote.rejected, (state, action) => {
        state.loadingState.openingQuoteLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateQuoteDiscount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuoteDiscount.fulfilled, (state, action) => {
        state.loading = false;
        const updatedQuote = action.payload.quotation;
        if (!updatedQuote?._id) return;
        state.epcQuotes = state.epcQuotes.map((quote) =>
          quote._id === updatedQuote._id ? updatedQuote : quote,
        );
      })
      .addCase(updateQuoteDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadCustomerDocument.pending, (state) => {
        state.loadingState.uploadingDocumentLoading = true;
        state.error = null;
      })
      .addCase(uploadCustomerDocument.fulfilled, (state, action) => {
        state.loadingState.uploadingDocumentLoading = false;
      })
      .addCase(uploadCustomerDocument.rejected, (state, action) => {
        state.loadingState.uploadingDocumentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addRepaymentTask.pending, (state) => {
        state.loadingState.addingRepaymentLoading = true;
        state.error = null;
      })
      .addCase(addRepaymentTask.fulfilled, (state, action) => {
        state.loadingState.addingRepaymentLoading = false;
      })
      .addCase(addRepaymentTask.rejected, (state, action) => {
        state.loadingState.addingRepaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProductRegistration.pending, (state) => {
        state.loadingState.updatingProductRegistrationLoading = true;
        state.error = null;
      })
      .addCase(updateProductRegistration.fulfilled, (state, action) => {
        state.loadingState.updatingProductRegistrationLoading = false;

        const updatedCustomer = action.payload;

        const customerIndex = state.registeredUserInfoWithDetails.findIndex(
          (customer) => customer._id === updatedCustomer._id,
        );

        if (customerIndex !== -1) {
          state.registeredUserInfoWithDetails[customerIndex] = {
            ...state.registeredUserInfoWithDetails[customerIndex],
            productRegistration: updatedCustomer.productRegistration,
          };
        }
      })
      .addCase(updateProductRegistration.rejected, (state, action) => {
        state.loadingState.updatingProductRegistrationLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateKycDocument.pending, (state, action) => {
        const { documentId } = action.meta.arg;
        state.loadingState.verifyingDocumentLoading[documentId] = true;
        state.error = null;
      })
      .addCase(updateKycDocument.fulfilled, (state, action) => {
        const { documentId } = action.meta.arg;
        state.loadingState.verifyingDocumentLoading[documentId] = false;

        const updatedCustomerId = action.payload._id;
        const updatedCustomerData = action.payload;

        const index = state.registeredUserInfoWithDetails.findIndex(
          (c) => c._id === updatedCustomerId,
        );

        if (index !== -1) {
          state.registeredUserInfoWithDetails[index] = {
            ...state.registeredUserInfoWithDetails[index],
            ...updatedCustomerData,
          };
        }
      })
      .addCase(updateKycDocument.rejected, (state, action) => {
        const { documentId } = action.meta.arg;
        state.loadingState.verifyingDocumentLoading[documentId] = false;
        state.error = action.payload as string;
      })
      .addCase(addEpcPayment.pending, (state) => {
        state.loadingState.addingPaymentLoading = true;
        state.error = null;
      })
      .addCase(addEpcPayment.fulfilled, (state, action) => {
        state.loadingState.addingPaymentLoading = false;

        // Update the current customer's payments if it matches
        if (state.currentEpcCustomer.customerPayment) {
          const updatedPayment = action.payload;

          // If it's an existing payment with new entry
          if (
            state.currentEpcCustomer.customerPayment._id === updatedPayment._id
          ) {
            state.currentEpcCustomer.customerPayment = updatedPayment;
          }
        }

        // Also update the allPayments array
        if (state.currentEpcCustomer.allPayments) {
          const existingIndex = state.currentEpcCustomer.allPayments.findIndex(
            (p) => p._id === action.payload._id,
          );

          if (existingIndex !== -1) {
            state.currentEpcCustomer.allPayments[existingIndex] =
              action.payload;
          } else {
            state.currentEpcCustomer.allPayments.push(action.payload);
          }
        }
      })
      .addCase(addEpcPayment.rejected, (state, action) => {
        state.loadingState.addingPaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateQuoteAdjustmentAmount.pending, (state) => {
        state.loadingState.updatingAdjustmentAmountLoading = true;
        state.error = null;
      })
      .addCase(updateQuoteAdjustmentAmount.fulfilled, (state, action) => {
        state.loadingState.updatingAdjustmentAmountLoading = false;

        const updatedQuote = action.payload;
        if (updatedQuote?._id) {
          state.epcQuotes = state.epcQuotes.map((quote) =>
            quote._id === updatedQuote._id ? updatedQuote : quote,
          );
          state.registeredUserInfoWithDetails =
            state.registeredUserInfoWithDetails.map((customer) => {
              if (customer.quote?._id === updatedQuote._id) {
                return {
                  ...customer,
                  quote: updatedQuote,

                  currentPayment: customer.currentPayment,
                };
              }
              return customer;
            });
        }
      })
      .addCase(updateQuoteAdjustmentAmount.rejected, (state, action) => {
        state.loadingState.updatingAdjustmentAmountLoading = false;
        state.error = action.payload as string;
      })
      // Add this to your extraReducers builder
      .addCase(updateRegistrationStatus.pending, (state) => {
        state.loadingState.updatingRegistrationStatusLoading = true;
        state.error = null;
      })
      .addCase(updateRegistrationStatus.fulfilled, (state, action) => {
        state.loadingState.updatingRegistrationStatusLoading = false;

        // Update the specific customer in the list
        const updatedCustomer = action.payload;
        if (updatedCustomer?._id) {
          const index = state.registeredUserInfoWithDetails.findIndex(
            (c) => c._id === updatedCustomer._id,
          );
          if (index !== -1) {
            state.registeredUserInfoWithDetails[index] = {
              ...state.registeredUserInfoWithDetails[index],
              registrationStatus: updatedCustomer.registrationStatus,
              registrationRemark: updatedCustomer.registrationRemark,
            };
          }
        }
      })
      .addCase(updateRegistrationStatus.rejected, (state, action) => {
        state.loadingState.updatingRegistrationStatusLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addRoundOffToPayment.pending, (state) => {
        state.loadingState.addingRoundOffLoading = true;
        state.error = null;
      })
      .addCase(addRoundOffToPayment.fulfilled, (state, action) => {
        state.loadingState.addingRoundOffLoading = false;

        // Update the customer's payment in the registered users list
        const updatedPayment = action.payload;

        // Find and update the customer in registeredUserInfoWithDetails
        const customerIndex = state.registeredUserInfoWithDetails.findIndex(
          (customer) =>
            customer.mobileNumber === updatedPayment.mobileNumber ||
            customer.payments?.some((p) => p._id === updatedPayment._id),
        );

        if (customerIndex !== -1) {
          // Update payments array if it exists
          if (state.registeredUserInfoWithDetails[customerIndex].payments) {
            const paymentIndex = state.registeredUserInfoWithDetails[
              customerIndex
            ].payments!.findIndex((p) => p._id === updatedPayment._id);
            if (paymentIndex !== -1) {
              state.registeredUserInfoWithDetails[customerIndex].payments![
                paymentIndex
              ] = updatedPayment;
            }
          }

          // Update currentPayment if it exists
          if (
            state.registeredUserInfoWithDetails[customerIndex].currentPayment
              ?._id === updatedPayment._id
          ) {
            state.registeredUserInfoWithDetails[customerIndex].currentPayment =
              updatedPayment;
          }
        }
      })
      .addCase(addRoundOffToPayment.rejected, (state, action) => {
        state.loadingState.addingRoundOffLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getSingleEpcQuoteThunk.pending, (state) => {
        state.loadingState.addingEpcOrderLoading = true;
      })
      .addCase(getSingleEpcQuoteThunk.fulfilled, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(getSingleEpcQuoteThunk.rejected, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(addEpcOrderThunk.pending, (state) => {
        state.loadingState.addingEpcOrderLoading = true;
      })
      .addCase(addEpcOrderThunk.fulfilled, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(addEpcOrderThunk.rejected, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(updateEpcOrderThunk.pending, (state) => {
        state.loadingState.addingEpcOrderLoading = true;
      })
      .addCase(updateEpcOrderThunk.fulfilled, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(updateEpcOrderThunk.rejected, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(confirmEpcOrderThunk.pending, (state) => {
        state.loadingState.addingEpcOrderLoading = true;
      })
      .addCase(confirmEpcOrderThunk.fulfilled, (state) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(confirmEpcOrderThunk.rejected, (state, action) => {
        state.loadingState.addingEpcOrderLoading = false;
      })
      .addCase(getSpecificRoleUsers.pending, (state) => {
        state.loadingState.fetchingSpecificRoleUsersLoading = true;
        state.error = null;
      })
      .addCase(getSpecificRoleUsers.fulfilled, (state, action) => {
        state.loadingState.fetchingSpecificRoleUsersLoading = false;
        state.specificRoleUsers = action.payload;
      })
      .addCase(getSpecificRoleUsers.rejected, (state, action) => {
        state.loadingState.fetchingSpecificRoleUsersLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSalesRepAssigneeLogs.pending, (state) => {
        state.loadingState.fetchingAssigneeLogsLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesRepAssigneeLogs.fulfilled, (state, action) => {
        state.loadingState.fetchingAssigneeLogsLoading = false;
        state.salesRepAssigneeLogs = action.payload;
      })
      .addCase(fetchSalesRepAssigneeLogs.rejected, (state, action) => {
        state.loadingState.fetchingAssigneeLogsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOtherInverter.pending, (state) => {
        state.loadingState.updatingOtherInverterLoading = true;
        state.error = null;
      })
      .addCase(updateOtherInverter.fulfilled, (state) => {
        state.loadingState.updatingOtherInverterLoading = false;
      })
      .addCase(updateOtherInverter.rejected, (state, action) => {
        state.loadingState.updatingOtherInverterLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSalesRepsUnderCSE.pending, (state) => {
        state.loadingState.fetchingCSEWithSalesRepsLoading = true;
      })
      .addCase(fetchSalesRepsUnderCSE.fulfilled, (state, action) => {
        state.loadingState.fetchingCSEWithSalesRepsLoading = false;
        state.cseWithSalesReps = action.payload;
      })
      .addCase(fetchSalesRepsUnderCSE.rejected, (state, action) => {
        state.loadingState.fetchingCSEWithSalesRepsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getCustomerByMobileNumber.pending, (state) => {
        state.loadingState.fetchingCustomerByMobileNumberLoading = true;
        state.error = null;
      })
      .addCase(getCustomerByMobileNumber.fulfilled, (state, action) => {
        state.loadingState.fetchingCustomerByMobileNumberLoading = false;
        state.customerByMobileNumber = action.payload;
      })
      .addCase(getCustomerByMobileNumber.rejected, (state, action) => {
        state.loadingState.fetchingCustomerByMobileNumberLoading = false;
        state.error = action.payload as string;
      })
      .addCase(activateQuote.pending, (state) => {
        state.loadingState.activateQuoteloading = true;
        state.error = null;
      })
      .addCase(activateQuote.fulfilled, (state, action) => {
        state.loadingState.activateQuoteloading = false;
        state.error = null;
        state.registeredCustomers.push(action.payload.data);
      })
      .addCase(activateQuote.rejected, (state, action) => {
        state.loadingState.activateQuoteloading = false;
        state.error =
          (action.payload as string) || "Failed to activate quote for customer";
      })
      .addCase(deductSalesRepCommission.pending, (state) => {
        state.loadingState.deductSalesRepCommissionLoading = true;
        state.error = null;
      })
      .addCase(deductSalesRepCommission.fulfilled, (state) => {
        state.loadingState.deductSalesRepCommissionLoading = false;
      })
      .addCase(deductSalesRepCommission.rejected, (state, action) => {
        state.loadingState.deductSalesRepCommissionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentEpcCustomer,
  setSearchTerm,
  setSelectedSalesRep,
  clearSearchAndSelectedSalesRep,
  setSelectedCseIdForCustomerDetails,
  resetCreateEpcQuoteState,
  setSelectedCSE,
  setSalesRepOptions,
  setSelectedStatus,
} = epcSlice.actions;
export default epcSlice.reducer;
